const WEEKDAY_LABELS = {
  MO: 'Monday',
  TU: 'Tuesday',
  WE: 'Wednesday',
  TH: 'Thursday',
  FR: 'Friday',
  SA: 'Saturday',
  SU: 'Sunday',
}

const MONTH_LABELS = [
  '',
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

const ORDINAL_LABELS = {
  1: 'first',
  2: 'second',
  3: 'third',
  4: 'fourth',
  '-1': 'last',
}

const joinListWithAnd = (items) => {
  if (!Array.isArray(items) || items.length === 0) {
    return ''
  }

  if (items.length === 1) {
    return items[0]
  }

  if (items.length === 2) {
    return `${items[0]} and ${items[1]}`
  }

  return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`
}

const parseRrule = (rrule) => {
  if (typeof rrule !== 'string' || rrule.trim() === '') {
    return null
  }

  const result = {}
  const chunks = rrule
    .split(';')
    .map((chunk) => chunk.trim())
    .filter((chunk) => chunk !== '')

  for (const chunk of chunks) {
    const [rawKey, ...valueParts] = chunk.split('=')
    const key = rawKey?.trim()?.toUpperCase()
    const value = valueParts.join('=').trim()

    if (!key || !value) {
      continue
    }

    result[key] = value
  }

  return result
}

const parseUntil = (untilValue) => {
  if (typeof untilValue !== 'string' || untilValue.trim() === '') {
    return null
  }

  const value = untilValue.trim()

  if (/^\d{8}$/.test(value)) {
    const year = Number(value.slice(0, 4))
    const month = Number(value.slice(4, 6)) - 1
    const day = Number(value.slice(6, 8))
    return new Date(Date.UTC(year, month, day))
  }

  if (/^\d{8}T\d{6}Z$/.test(value)) {
    const year = Number(value.slice(0, 4))
    const month = Number(value.slice(4, 6)) - 1
    const day = Number(value.slice(6, 8))
    const hour = Number(value.slice(9, 11))
    const minute = Number(value.slice(11, 13))
    const second = Number(value.slice(13, 15))
    return new Date(Date.UTC(year, month, day, hour, minute, second))
  }

  const parsedDate = new Date(value)

  if (Number.isNaN(parsedDate.getTime())) {
    return null
  }

  return parsedDate
}

const formatUntilDate = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return null
  }

  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(date)
}

const parseByDayEntries = (rawByDay) => {
  if (typeof rawByDay !== 'string' || rawByDay.trim() === '') {
    return []
  }

  return rawByDay
    .split(',')
    .map((entry) => entry.trim().toUpperCase())
    .filter((entry) => entry !== '')
    .map((entry) => {
      const matches = entry.match(/^([+-]?\d)?([A-Z]{2})$/)

      if (!matches) {
        return { code: entry, ordinal: null }
      }

      return {
        code: matches[2],
        ordinal: matches[1] ? Number(matches[1]) : null,
      }
    })
}

const humanizeByDay = (rawByDay) => {
  const entries = parseByDayEntries(rawByDay)

  if (entries.length === 0) {
    return null
  }

  const labels = entries.map((entry) => {
    const weekday = WEEKDAY_LABELS[entry.code] ?? entry.code

    if (entry.ordinal === null || Number.isNaN(entry.ordinal)) {
      return weekday
    }

    const ordinalLabel = ORDINAL_LABELS[entry.ordinal]

    if (!ordinalLabel) {
      return weekday
    }

    return `${ordinalLabel} ${weekday}`
  })

  return joinListWithAnd(labels)
}

const humanizeBaseFrequency = (freq, interval) => {
  const normalizedInterval = Number.isInteger(interval) && interval > 0 ? interval : 1

  if (freq === 'DAILY') {
    return normalizedInterval === 1 ? 'Every day' : `Every ${normalizedInterval} days`
  }

  if (freq === 'WEEKLY') {
    return normalizedInterval === 1 ? 'Every week' : `Every ${normalizedInterval} weeks`
  }

  if (freq === 'MONTHLY') {
    return normalizedInterval === 1 ? 'Every month' : `Every ${normalizedInterval} months`
  }

  if (freq === 'YEARLY') {
    return normalizedInterval === 1 ? 'Every year' : `Every ${normalizedInterval} years`
  }

  return 'Custom recurrence'
}

const humanizeMonthlyOrYearlyAnchors = (parts, freq) => {
  const byMonthDayRaw = parts.BYMONTHDAY
  const byMonthRaw = parts.BYMONTH
  const byDayRaw = parts.BYDAY

  if (typeof byMonthDayRaw === 'string' && byMonthDayRaw.trim() !== '') {
    const byMonthDayValues = byMonthDayRaw
      .split(',')
      .map((value) => Number(value))
      .filter((value) => Number.isInteger(value))

    if (byMonthDayValues.length > 0) {
      if (freq === 'YEARLY' && typeof byMonthRaw === 'string') {
        const byMonthValue = Number(byMonthRaw)
        const monthName = MONTH_LABELS[byMonthValue] ?? null

        if (monthName) {
          return `on ${monthName} ${byMonthDayValues[0]}`
        }
      }

      return `on day ${joinListWithAnd(byMonthDayValues.map((value) => String(value)))}`
    }
  }

  const byDayLabel = humanizeByDay(byDayRaw)

  if (byDayLabel) {
    return `on ${byDayLabel}`
  }

  if (freq === 'YEARLY' && typeof byMonthRaw === 'string') {
    const byMonthValue = Number(byMonthRaw)
    const monthName = MONTH_LABELS[byMonthValue] ?? null

    if (monthName) {
      return `in ${monthName}`
    }
  }

  return null
}

const humanizeTermination = (parts) => {
  const countRaw = parts.COUNT
  const untilRaw = parts.UNTIL

  if (typeof countRaw === 'string') {
    const count = Number(countRaw)

    if (Number.isInteger(count) && count > 0) {
      return `for ${count} occurrence${count === 1 ? '' : 's'}`
    }
  }

  if (typeof untilRaw === 'string') {
    const untilDate = parseUntil(untilRaw)
    const formattedDate = formatUntilDate(untilDate)

    if (formattedDate) {
      return `until ${formattedDate}`
    }
  }

  return null
}

export const useScheduleHumanReadable = () => {
  const toHumanReadableRrule = (rrule) => {
    const parts = parseRrule(rrule)

    if (!parts || !parts.FREQ) {
      return 'No recurrence configured.'
    }

    const freq = String(parts.FREQ).toUpperCase()
    const interval = Number(parts.INTERVAL)
    let description = humanizeBaseFrequency(freq, interval)

    if (freq === 'WEEKLY') {
      const byDayLabel = humanizeByDay(parts.BYDAY)

      if (byDayLabel) {
        description = `${description} on ${byDayLabel}`
      }
    }

    if (freq === 'MONTHLY' || freq === 'YEARLY') {
      const anchorLabel = humanizeMonthlyOrYearlyAnchors(parts, freq)

      if (anchorLabel) {
        description = `${description} ${anchorLabel}`
      }
    }

    const terminationLabel = humanizeTermination(parts)

    if (terminationLabel) {
      description = `${description}, ${terminationLabel}`
    }

    return description
  }

  return {
    toHumanReadableRrule,
  }
}
